import {
  ApeironInfinityDataGrid,
  ApeironInfinityDataGridRef,
  InfinityDatasourceResult,
  MayNull,
  NextImage,
  noForwardProps,
  SkeletonBox,
  SkeletonTypography,
  useAccountContext
} from '@apeiron/temp'
import { ApeironSimpleButton, ApeironTooltipSection } from '@apeiron/ui'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import MoreSVG from '@public/icons/icon-guild-more.svg'
import InfoSVG from '@public/icons/icon-info.svg'
import BattleDemoRewardTips from '@src/components/battle/demo/leaderboard/BattleDemoRewardTips'
import {
  BattleDemoNameCell,
  BattleDemoRanking,
  BattleDemoRewardCell
} from '@src/components/battle/demo/leaderboard/BattleDemoSharedComponent'
import GuildChangePopup from '@src/components/guild/change/GuildChangePopup'
import ApeironYouLabel from '@src/components/share/apeiron/ApeironYouLabel'
import Countdown from '@src/components/share/generic/Countdown'
import Dropdown from '@src/components/share/generic/Dropdown'
import {
  GUILD_WAR_LEADERBOARD_NO_VALUE,
  REWARD_TOOLTIP_ID
} from '@src/constants/battleDemo'
import { GuildType } from '@src/constants/guild'
import { useGuildChangeContext } from '@src/contexts/guild/GuildChangeContext'
import useGetBattleDemoGuildWarPvPLeaderboard from '@src/hooks/graphql/useGetBattleDemoGuildWarPvPLeaderboard'
import useGetBattleDemoMyGuildWarPvPLeaderboard from '@src/hooks/graphql/useGetBattleDemoMyGuildWarPvPLeaderboard'
import useGetBattleDemoPvPSeasons from '@src/hooks/graphql/useGetBattleDemoPvPSeasons'
import theme from '@src/styles/theme'
import {
  BattleDemoGuildPvPLeaderboard,
  BattleDemoGuildPvPRank,
  BattleDemoSeason
} from '@src/types/battleDemo'
import {
  BodyScrollEvent,
  CellClassRules,
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
  RowClassRules
} from 'ag-grid-community'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAsyncEffect from 'use-async-effect'

const YouLabel = styled(ApeironYouLabel)`
  position: fixed;
`

const DataGrid = styled(ApeironInfinityDataGrid)`
  ${props => props.theme.breakpoints.up('res1024')} {
    height: calc(100% - 40px);
    width: 100%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    height: calc(100% - 20px);
    width: 100%;
  }
  overflow: visible;
  .Custom-Grid {
    overflow: visible;
    .ag-header {
      border-radius: 8px 8px 0px 0px;
      font-weight: 400;
      margin-bottom: -1px;
    }
    .ag-header-cell {
      padding: 0 8px;
    }
    .ag-header-row {
      background-color: #222035;
      font-size: 14px;
      font-weight: 300;
    }
    .ag-cell {
      ${props => props.theme.breakpoints.up('res1024')} {
        padding: 0 8px;
      }
      ${props => props.theme.breakpoints.down('res1024')} {
      }
      border-bottom: 1px solid #28272b77;
      overflow: visible;
    }
    .custom-player-row {
      border-bottom: 4px solid #267ff8;
      border-top: 4px solid #267ff8;
      background-color: #0e0e12;
    }
    .custom-player-start-cell {
      border: unset;
      border-left: 4px solid #267ff8;
      background-color: #0e0e12;
    }
    .custom-player-end-cell {
      background-color: #0e0e12;
      border-right: 4px solid #267ff8;
      border: unset;
      overflow: visible;
    }
  }
`

const Text = styled(Typography, noForwardProps)<TextProps>`
  font-size: 18px;
  font-weight: 300;
  line-height: 18px;
  ${props => (props.__disabled ? 'color: #939494;' : '')}
`

const Container = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0px 20px;
  max-width: 1140px;
  width: 100%;
`

const SeasonContainer = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`

const SeasonDropdown = styled(Dropdown)``

const DataGridContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 40px;
  min-height: 650px;
  width: 100%;
`

const CountdownContainer = styled(Box)`
  background-color: #222035;
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 780px;
  max-width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    gap: 40px;
    padding: 25px 0px 10px 0px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    gap: 10px;
    padding: 20px 0px 5px 0px;
  }
`

const CountdownEndedContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: 0px 20px;
`

const CountdownTitle = styled(SkeletonTypography)`
  font-size: 48px;
  font-weight: 400;
  line-height: 55px;
  min-width: 70%;
  text-align: center;
  text-transform: uppercase;
`

const CountdownSubtitle = styled(SkeletonBox)`
  border-radius: 10px;
  font-family: var(--font-Apeiron);
  font-size: 24px;
  font-weight: 400;
  line-height: 26px;
  min-width: 60%;
  text-align: center;
`

const MoreButtonContainer = styled(Box)`
  height: 100%;
  position: fixed;
  right: 0px;
  border-left: 5px solid #267ff8;
  border-right: 5px solid #267ff8;
`

const MoreButton = styled(ApeironSimpleButton)`
  background-color: #267ff8;
  border-radius: unset;
  width: 100%;
  height: 100%;

  &.MuiButtonBase-root {
    min-width: unset;
    padding: 0px 16px;
  }
  :disabled {
    padding: 2px;
    pointer-events: none;
  }
`

const MoreIcon = styled(NextImage)`
  aspect-ratio: 1;
  width: 20px;
`

const TooltipHeaderContainer = styled(ApeironTooltipSection)`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
`

const InfoImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 16px;
`

const CellText: FC<CellTextProps> = props => {
  const { className, disabled, value } = props

  return (
    <Text className={className} __disabled={disabled}>
      {value}
    </Text>
  )
}

const GuildMore: FC<GuildMoreProps> = props => {
  const { rank, myRank, onClick } = props

  const inGuild = rank?.guild.id === myRank?.guild.id

  return inGuild ? (
    <MoreButtonContainer>
      <MoreButton onClick={onClick}>
        <MoreIcon src={MoreSVG} />
      </MoreButton>
    </MoreButtonContainer>
  ) : (
    <></>
  )
}

const RewardHeader: FC<RewardHeaderProps> = props => {
  const { t } = useTranslation()

  const { season } = props

  return (
    <TooltipHeaderContainer
      stay
      tooltip={
        <BattleDemoRewardTips rewardGroups={season?.rewardGroups || []} />
      }
      tooltipId={REWARD_TOOLTIP_ID}
    >
      {t('battle_demo.rewards')}
      <InfoImage src={InfoSVG} />
    </TooltipHeaderContainer>
  )
}

const BattleDemoGuildWarPvPLeaderboard: FC<Props> = props => {
  const { className } = props

  const ref = useRef<ApeironInfinityDataGridRef | null>(null)

  const [leaderboard, setLeaderboard] =
    useState<MayNull<BattleDemoGuildPvPLeaderboard>>(null)

  const [selectedSeason, setSelectedSeason] =
    useState<MayNull<BattleDemoSeason>>(null)

  const [myRowInList, setMyRowInList] = useState<boolean>(false)

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const { account, isLoggedIn } = useAccountContext()

  const { setGuildType } = useGuildChangeContext()

  const { fetch: fetchRecords, loading: loadingRecords } =
    useGetBattleDemoGuildWarPvPLeaderboard()

  const { fetch: fetchMyRecords, loading: loadingMyRecords } =
    useGetBattleDemoMyGuildWarPvPLeaderboard()

  const { data: seasons, fetch: fetchSeasons } = useGetBattleDemoPvPSeasons()

  const fetch = isLoggedIn ? fetchMyRecords : fetchRecords

  const loading = isLoggedIn ? loadingMyRecords : loadingRecords

  const endTime = useMemo(
    () => selectedSeason?.end || '0',
    [selectedSeason?.end]
  )

  const isEnded = useMemo(
    () =>
      endTime === '0'
        ? true
        : new Date().getTime() > new Date(endTime).getTime(),
    [endTime]
  )

  const emptyUserRow = useMemo(() => {
    return {
      guild: {
        name: t('battle_demo.not_in_guild'),
        icon: '',
        id: '-1',
        tag: 0
      },
      id: '-1',
      matchMakingRate: -1,
      rank: -1,
      totalStar: -1
    }
  }, [t])

  const myRank = useMemo((): MayNull<BattleDemoGuildPvPRank> => {
    return leaderboard?.playerRank || emptyUserRow
  }, [emptyUserRow, leaderboard?.playerRank])

  const noReward = useMemo(
    () => R.isEmpty(selectedSeason?.rewardGroups || []),
    [selectedSeason?.rewardGroups]
  )

  const cellStartClassRules =
    useMemo((): CellClassRules<BattleDemoGuildPvPRank> => {
      return {
        'custom-player-start-cell': (
          params: RowClassParams<BattleDemoGuildPvPRank>
        ): boolean => {
          return params.data?.id === myRank?.id
        }
      }
    }, [myRank?.id])

  const cellEndClassRules =
    useMemo((): CellClassRules<BattleDemoGuildPvPRank> => {
      return {
        'custom-player-end-cell': (
          params: RowClassParams<BattleDemoGuildPvPRank>
        ): boolean => {
          return params.data?.id === myRank?.id
        }
      }
    }, [myRank?.id])

  const rowClassRules = useMemo((): RowClassRules<BattleDemoGuildPvPRank> => {
    return {
      'custom-player-row': (
        params: RowClassParams<BattleDemoGuildPvPRank>
      ): boolean => {
        return params.data?.id === myRank?.id
      }
    }
  }, [myRank?.id])

  const handleOnDropdownChange = useCallback(
    (value: string) => {
      setSelectedSeason(R.find(item => item.id === value, seasons))
    },
    [seasons]
  )

  const handleOnGuildMore = useCallback(() => {
    setGuildType(GuildType.PvP)
  }, [setGuildType])

  const handleOnTimerEnd = useCallback(() => {
    if (ref.current) {
      ref.current.refreshData()
    }
  }, [])

  const handleOnFetch = useCallback(
    async (
      page: number,
      pageSize: number
    ): Promise<InfinityDatasourceResult> => {
      if (R.isNil(selectedSeason)) {
        return { data: [], total: 0 }
      }

      const result = await fetch({
        first: pageSize,
        season: selectedSeason.id,
        offset: (page - 1) * pageSize
      })

      setLeaderboard(result.data)

      const ranks = R.pathOr([], ['data', 'ranks'], result)

      return {
        data: ranks,
        total: R.length(ranks)
      }
    },
    [fetch, selectedSeason]
  )

  const handleCheckPlayerInRow = useCallback(
    (api: GridApi, rank: MayNull<BattleDemoGuildPvPRank>) => {
      if (rank === null) return

      const { top: scrollTop, bottom: scrollBottom } =
        api.getVerticalPixelRange()

      const renderedNodes: IRowNode<BattleDemoGuildPvPRank>[] = R.filter(
        (rowNode: IRowNode) => {
          const { rowTop, rowHeight } = rowNode

          return (
            Number(rowTop) + Number(rowHeight) >= scrollTop &&
            Number(rowTop) <= scrollBottom
          )
        },
        api.getRenderedNodes()
      )

      const searchedNode = R.find(
        (node: IRowNode<BattleDemoGuildPvPRank>): boolean =>
          node.data?.id === myRank?.id
      )(renderedNodes)

      setMyRowInList(!R.isNil(searchedNode))
    },
    [myRank?.id]
  )

  const handleOnBodyScroll = useCallback(
    (event: BodyScrollEvent<InfinityDatasourceResult>) => {
      if (R.isNotNil(myRank)) {
        handleCheckPlayerInRow(event.api, myRank)
      }
    },
    [handleCheckPlayerInRow, myRank]
  )

  const handleOnGuildChanged = useCallback(() => {
    if (ref.current) {
      ref.current.refreshData()
    }
  }, [])

  useEffect(() => {
    if (ref.current) {
      if (R.isNotEmpty(selectedSeason)) {
        ref.current.refreshData()
      }
    }
  }, [isLoggedIn, account.walletAddress, selectedSeason])

  useAsyncEffect(async (isActive: () => boolean) => {
    const result = await fetchSeasons({
      isGuild: true
    })

    if (!isActive()) return

    const defaultSeason =
      R.find(R.propEq(true, 'active'), result) || R.head(result)

    setSelectedSeason(defaultSeason)
  }, [])

  const columns = useMemo((): ColDef<any, BattleDemoGuildPvPRank>[] => {
    return [
      {
        cellClassRules: cellStartClassRules,
        field: 'start',
        headerName: '',
        pinned: 'left',
        width: 20
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'rank',
        headerClass: 'custom-ag-header-center',
        headerName: '',
        pinned: 'left',
        width: 80,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          const inGuild = params.data?.id === myRank?.id

          return (
            <>
              {inGuild && <YouLabel />}
              <BattleDemoRanking record={params.data} />
            </>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-left',
        field: 'name',
        flex: 1,
        headerClass: 'custom-ag-header-left',
        headerName: t('battle_demo.name'),
        minWidth: mobile ? 180 : 200,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          return <BattleDemoNameCell record={params.data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'time',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('battle_demo.top_3_mmr'),
        minWidth: mobile ? 170 : 250,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          const rate = R.pathOr(
            GUILD_WAR_LEADERBOARD_NO_VALUE,
            ['data', 'matchMakingRate'],
            params
          ) as number

          const disabled = rate === GUILD_WAR_LEADERBOARD_NO_VALUE

          const value = disabled ? '-' : rate

          return <CellText disabled={disabled} value={value} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'totalStar',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('battle_demo.guild_total_star'),
        minWidth: mobile ? 170 : 250,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          const total = R.pathOr(
            GUILD_WAR_LEADERBOARD_NO_VALUE,
            ['data', 'totalStar'],
            params
          )

          const disabled = total === GUILD_WAR_LEADERBOARD_NO_VALUE

          const value = disabled ? '-' : total

          return <CellText disabled={disabled} value={value} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'reward',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        hide: noReward,
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          return (
            <BattleDemoRewardCell
              record={params.data}
              season={selectedSeason}
            />
          )
        },
        headerComponent: () => {
          return <RewardHeader season={selectedSeason} />
        }
      },
      {
        cellClassRules: cellEndClassRules,
        field: 'end',
        flex: 1,
        headerName: '',
        minWidth: 20
      },
      {
        field: 'change',
        headerName: '',
        width: 50,
        cellRenderer: (params: ICellRendererParams<BattleDemoGuildPvPRank>) => {
          return (
            <GuildMore
              rank={params.data}
              myRank={myRank}
              onClick={handleOnGuildMore}
            />
          )
        }
      }
    ]
  }, [
    cellStartClassRules,
    t,
    mobile,
    noReward,
    cellEndClassRules,
    myRank,
    selectedSeason,
    handleOnGuildMore
  ])

  return (
    <Container>
      <SeasonContainer>
        <SeasonDropdown
          items={seasons}
          value={selectedSeason?.id || ''}
          onDropdownChange={handleOnDropdownChange}
        />
      </SeasonContainer>
      <DataGridContainer>
        <CountdownContainer>
          {loading || isEnded ? (
            <CountdownEndedContainer>
              <CountdownTitle loading={loading}>
                {selectedSeason?.name}
              </CountdownTitle>
              <CountdownSubtitle loading={loading}>
                {selectedSeason?.name}
              </CountdownSubtitle>
            </CountdownEndedContainer>
          ) : (
            <Countdown targetDate={endTime} onComplete={handleOnTimerEnd} />
          )}
        </CountdownContainer>
        <DataGrid
          autoFetch={false}
          className={className}
          columnDefs={columns}
          gridRef={ref}
          headerHeight={65}
          pageSize={100}
          pinnedBottomRowData={
            myRowInList
              ? undefined
              : R.isNil(myRank)
              ? [emptyUserRow]
              : [myRank]
            // leaderboard?.ranks[1] ? [leaderboard?.ranks[1]] : undefined
          }
          rowClassRules={rowClassRules}
          rowHeight={62}
          fetch={handleOnFetch}
          onBodyScroll={handleOnBodyScroll}
        />
      </DataGridContainer>
      <GuildChangePopup onGuildChanged={handleOnGuildChanged} />
    </Container>
  )
}

type TextProps = {
  __disabled: boolean
}

type CellTextProps = {
  className?: string
  disabled: boolean
  value: string | number
}

type GuildMoreProps = {
  rank: MayNull<BattleDemoGuildPvPRank>
  myRank: MayNull<BattleDemoGuildPvPRank>
  onClick: () => void
}

type RewardHeaderProps = {
  season: MayNull<BattleDemoSeason>
}

type Props = {
  className?: string
}

export default BattleDemoGuildWarPvPLeaderboard
